import { combineURLs, DEFAULT_CDN_BASE_URL } from '@dvb/common';
import { Skeleton } from '@heroui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  type ImgHTMLAttributes,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

declare global {
  interface Window {
    _resImgLoadList: Record<string, boolean>;
  }
}

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  alt: string;
  fallbackUrl?: string;
  hasResponsive?: boolean;
  hasSkeleton?: boolean;
  isObjectContain?: boolean;
  radius?: number | string;
  ratio?: number;
  src?: string;
}

const MAX_DPI = 5;

const WHITELISTED_BASE_URLS = [
  `${DEFAULT_CDN_BASE_URL}/`,
  'https://api.dicebear.com/',
  'https://ipfs.io/ipfs/',
];

const getImgProxySrc = (originalSrc?: string, size?: number) => {
  const proxyURL = import.meta.env.VITE_APP_IMG_PROXY_URL;
  if (!proxyURL) return originalSrc;

  if (!originalSrc || !size) return '';
  return `${proxyURL}/no-s/rs:fill:${size}/plain/${encodeURIComponent(
    originalSrc
  )}`;
};

const isLoadFailed = (src: string) => {
  return window._resImgLoadList?.[src] === false;
};

function ResponsiveImage({
  alt,
  className,
  fallbackUrl = combineURLs(
    import.meta.env.VITE_APP_ASSET_BASE_URL,
    '/fallback/default.webp'
  ),
  hasResponsive = true,
  hasSkeleton = true,
  height,
  isObjectContain = false,
  loading,
  radius = undefined,
  ratio = 1,
  src: originalSrc = undefined,
  style,
  width,
  ...props
}: Props) {
  const [currentSrc, setCurrentSrc] = useState(String(originalSrc));
  const [isLoaded, setIsLoaded] = useState(() => {
    if (!window._resImgLoadList) window._resImgLoadList = {};
    return window._resImgLoadList[currentSrc] === true;
  });

  const setImgLoaded = useCallback((src: string) => {
    window._resImgLoadList[src] = true;
    setIsLoaded(true);
  }, []);

  const setImgLoadFailed = useCallback((src: string) => {
    window._resImgLoadList[src] = false;
    setIsLoaded(false);
  }, []);

  const srcSet = useMemo(() => {
    if (!import.meta.env.VITE_APP_IMG_PROXY_URL || !hasResponsive || !width)
      return undefined;

    if (!originalSrc || originalSrc.startsWith('blob:')) return undefined;

    if (isLoadFailed(currentSrc)) return undefined;

    // Only whitelist URL will enable responsive image
    if (!WHITELISTED_BASE_URLS.some((host) => originalSrc.startsWith(host)))
      return undefined;

    let result = '';
    for (let i = 1; i <= MAX_DPI; i += 1) {
      result += `${getImgProxySrc(originalSrc, Number(width) * i)} ${i}x,`;
    }
    return result;
  }, [currentSrc, hasResponsive, width, originalSrc]);

  const handleOnLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const src = (e.currentTarget as HTMLImageElement).currentSrc;
      /**
       * Default currentSrc is original image, we will update it if responsive image
       * or fallback image is loaded
       */
      if (currentSrc !== src) setCurrentSrc(src);
      if (src !== '') setImgLoaded(src);
    },
    [currentSrc, setImgLoaded]
  );

  const handleOnError = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const { currentTarget } = e;
      const src = (e.currentTarget as HTMLImageElement).currentSrc;

      /**
       * Prevent infinite request if fallback image also failed [Unexcepted]
       */
      if (src.endsWith(fallbackUrl)) return;

      if (src !== '') setImgLoadFailed(src);

      /**
       * If responsive image failed, we will try to load original image once
       * if still failed, load the fallback image
       */
      if (currentTarget.srcset) {
        if (originalSrc && !isLoadFailed(originalSrc)) {
          currentTarget.srcset = '';
          currentTarget.src = originalSrc;
        }
      } else currentTarget.src = fallbackUrl;
    },
    [fallbackUrl, originalSrc, setImgLoadFailed]
  );

  useEffect(() => {
    // Prevent changing src Skeleton do not show
    if (!originalSrc) setIsLoaded(false);
  }, [originalSrc]);

  return (
    <div
      className={className}
      style={{
        borderRadius: radius !== undefined ? radius : '0.75rem',
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
        ...style,
        aspectRatio: ratio,
        height,
        width,
      }}
      {...props}
    >
      <motion.img
        alt={alt}
        animate={isLoaded ? { opacity: 1 } : {}}
        initial={{ opacity: 0 }}
        loading={loading || 'lazy'}
        onError={handleOnError}
        onLoad={handleOnLoad}
        src={originalSrc}
        srcSet={srcSet}
        style={{
          aspectRatio: ratio,
          borderRadius: radius !== undefined ? radius : '0.75rem',
          fontSize: 0, // Hidden alternative text
          height: '100%',
          objectFit: isObjectContain ? 'contain' : 'cover',
          padding: 0.5, // Prevent showing outline
          width: '100%',
        }}
      />
      {hasSkeleton && (
        <AnimatePresence>
          {!isLoaded && (
            <motion.div
              className="absolute inset-0 size-full overflow-hidden"
              exit={{ opacity: 0, transition: { duration: 1 } }}
              initial={{ opacity: 1 }}
            >
              <Skeleton className="bg-default-300 size-full" />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}

export { ResponsiveImage };
